<template>
    <SkuStoneSearch
        v-model:sorting="sorting"
        :active-filter-count
        :bounds
        :category
        :data
        :errors
        :fetching
        :filter-chips
        :filters="filtersVariable"
        :get-sort-route
        :get-stone-route
        :has-bad-facets
        :hash-params
        :page
        :pagination-to
        :products-per-page="limit"
        :response
        :route-reset
        :shape
        :stale
        :title="
            GemsApi_StoneCategory.Diamond === category
                ? t('diamond_search.diamond_search')
                : t('diamond_search.lab_grown_diamond_search')
        "
        @close-stone-modal="closeStoneModal"
    >
        <template #before-filters>
            <CmsBlocks
                :data="data?.pageProductListing?.beforeFilters || []"
                page-type="product-listing"
            />
        </template>

        <template #filters-quick>
            <ShapeFilter
                v-if="shapes"
                v-qa="'filter-shape'"
                :get-route="getRouteShape"
                :locked-to="ringShape"
                :selected-shape="shape"
                :shapes
            />
        </template>

        <template #after-grid>
            <CmsBlocks
                :data="data?.pageProductListing?.afterGrid || []"
                page-type="product-listing"
            />
        </template>

        <template #filters-chips>
            <FilterChips
                v-qa="'filter-chips-filter'"
                :chips="filterChips"
                :route-reset
            />
        </template>

        <template #filters>
            <DiamondFilters
                v-if="bounds"
                v-model:values="filters"
                :bounds
                :category
                :shape
            >
                <template #stone-type>
                    <StoneSearchTabs
                        :shape="gemsApiShapeToShapeCode(shape)"
                        class="tabs"
                        data-qa="stone-search-tabs"
                    />
                </template>

                <template #price>
                    <FilterPriceRange
                        v-if="bounds && priceBounds"
                        v-model="priceInCurrency"
                        v-qa="'filter-price'"
                        :bounds="priceBounds.bounds"
                        :currency="priceBounds.currency"
                    />
                </template>
            </DiamondFilters>
        </template>

        <template #last-page-filters>
            <DiamondFilters
                v-if="bounds"
                v-model:values="filters"
                :bounds
                :category
                :shape
            >
                <template #price>
                    <FilterPriceRange
                        v-if="bounds && priceBounds"
                        v-model="priceInCurrency"
                        v-qa="'filter-price'"
                        :bounds="priceBounds.bounds"
                        :currency="priceBounds.currency"
                    />
                </template>
            </DiamondFilters>
        </template>

        <template #no-results-found>
            <ThTitle
                alignment="CENTER"
                color="var(--blue)"
                size="SMALL"
                tag="H3"
            >
                We couldn't find any natural diamonds matching your current
                search criteria.
            </ThTitle>

            <ThSpacer size="SMALL" />

            <ThText
                alignment="CENTER"
                color="var(--slate)"
                size="MEDIUM"
            >
                <p>
                    You are most likely to find results by tweaking your
                    <b>price</b> and/or <b>carat weight</b> filters.
                </p>

                <p>
                    Please
                    <ThLink @click="openSpeakToExpertModal">contact us</ThLink>
                    if you have a specific diamond search request and we can
                    help you with your enquiry.
                </p>
            </ThText>
        </template>
    </SkuStoneSearch>
</template>

<script lang="ts" setup>
import {
    computed,
    type ComputedRef,
    defineAsyncComponent,
    hydrateOnVisible,
    toRef,
} from 'vue'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { decodeFiltersAndStoneIdentifier, usePriceBounds } from '@/domain/stone'
import {
    type DiamondSearchFilters,
    useDiamondSearchBounds,
    useNotClarities,
    useNotColours,
    useNotSymmetries,
} from '@/domain/stone/diamond'

import {
    formatNumericProperty,
    replace_,
    replaceDIAMOND_,
} from '@/domain/stone/labels'

import type {
    GemsApi_StoneShape,
    GemsApi_StoneSorting,
    GemsFindStonesQueryVariables,
} from '@/graphql/api'
import { GemsApi_StoneCategory } from '@/graphql/api'
import { useResponseError } from '@/helpers/errors'
import { useTranslator } from '@/i18n'
import { useCountryPlugin } from '@/plugins/country.ts'
import { useRobotsPlugin } from '@/plugins/robots'
import { useRoutes } from '@/router/routes'
import { defaultCurrency } from '@/plugins/currency'
import { useRingShapeConstraintForGemsAPI } from '@/domain/stone/search/useRingShapeConstraintForGemsAPI.ts'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useActivated } from '@/composables/useActivated'
import { useFiltersCount } from '@/views/product/useFiltersCount'
import {
    getCollectionOverallFilters,
    hasFiltersThatPreventCollection,
} from '@/domain/stone/search/sku/collectionOverall'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import FilterChips from '@/components/ui/filter/FilterChips.vue'
import { getClearFilter } from '@/helpers/filter'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'
import { systemCodeToWords } from '@/helpers'
import { useObjectComputed } from '@/composables/useObjectComputed.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useIsAdmin } from '@/components/app/isAdmin.ts'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape'
import { usePageWatcher } from '@/composables/page-watcher.ts'
import { allowedShapes } from '@/domain/stone/search/allowed-shapes.ts'
import { useGemsFindStonesQuery } from '@/graphql/api/stone/gems/GemsFindStones.query.generated.ts'
import { refDebounced } from '@vueuse/core'

import SkuStoneSearch from '@/components/stone/search/sku/SkuStoneSearch.vue'
import DiamondFilters from '@/components/stone/search/sku/DiamondFilters.vue'
import FilterPriceRange from '@/components/stone/search/sku/filter/FilterPriceRange.vue'
import ShapeFilter from '@/components/stone/search/ShapeFilter.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThText from '@/components/ui/ThText.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import { useWatchRedirectImmediate } from '@/helpers/redirect.ts'

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    category: Extract<
        GemsApi_StoneCategory,
        | typeof GemsApi_StoneCategory.Diamond
        | typeof GemsApi_StoneCategory.LabGrownDiamond
    >
    shape?: GemsApi_StoneShape
    page: number
}>()

const router = useRouter()
const { countryCode } = useCountryPlugin()
const { locale } = useTranslator()
const currency = useCurrencyInput()
const routes = useRoutes()
const notColours = useNotColours()
const notClarities = useNotClarities()
const notSymmetries = useNotSymmetries()
const robots = useRobotsPlugin()
const { t } = useI18n()
const isAdmin = useIsAdmin()

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

robots.set({
    index: true,
    follow: false,
})

const hashParams = computed(() =>
    decodeFiltersAndStoneIdentifier(router.currentRoute.value.hash.substring(1))
)

const defaultFilters: Required<DiamondSearchFilters> = {
    colour: [],
    clarity: [],
    polish: [],
    fluorescence: [],
    symmetry: [],
    cut: [],

    hasPhoto: null,
    isCanadaMark: null,
    withOriginCountry: null,

    priceInCurrency: null,
    weight: null,
    depthPercent: null,
    tablePercent: null,
    ratio: null,

    sorting: null,

    length: null,
    width: null,
    depth: null,
    deliveryDays: null,

    isStock: null,
    isDtlReport: null,
    shippingFrom: null,
    originCountry: null,
}

const hashFilters = toRef(() => hashParams.value?.filters)

const filters = computed({
    get: (): Required<DiamondSearchFilters> => {
        if (hashFilters.value) {
            return { ...defaultFilters, ...hashFilters.value }
        }

        return defaultFilters
    },
    set: (filters: DiamondSearchFilters) => {
        router.push(
            routes.diamondSearch(
                props.category,
                props.shape,
                filters,
                undefined,
                hashParams.value?.stoneIdentifier
            )
        )
    },
})

const filtersDebounced = refDebounced(filters, 500)

const sorting = computed({
    get(): GemsApi_StoneSorting {
        return filters.value.sorting || { field: 'PRICE', direction: 'ASC' }
    },
    set(v: GemsApi_StoneSorting): void {
        filters.value = {
            ...filters.value,
            sorting: v,
        }
    },
})

const formattedPriceRange = useFormattedPriceRange(
    toRef(() => filters.value.priceInCurrency)
)

const getSortRoute = (sorting: GemsApi_StoneSorting): ButtonRouteRelative =>
    routes.diamondSearch(props.category, props.shape, {
        ...filters.value,
        sorting,
    })

const priceInCurrency = computed({
    get: () => filters.value.priceInCurrency,
    set: (priceInCurrency) => {
        filters.value = { ...filters.value, priceInCurrency }
    },
})

const limit = 47

const getRouteShape = (shape?: GemsApi_StoneShape) => {
    const filtersCopied = { ...filters.value }

    if (shape && shape !== 'ROUND') {
        filtersCopied.cut = null
    }

    return routes.diamondSearch(
        props.category,
        shape,
        filtersCopied,
        undefined,
        hashParams.value?.stoneIdentifier
    )
}

const redirect = useRedirect()

const filtersVariable = useObjectComputed(() => ({
    ...filtersDebounced.value,
    ...(!hasFiltersThatPreventCollection(hashFilters.value) && !isAdmin.value
        ? getCollectionOverallFilters(props.shape)
        : {}),
    sorting: {
        direction: filtersDebounced.value?.sorting?.direction || 'ASC',
        field: filtersDebounced.value?.sorting?.field || 'PRICE',
    },
    notColour: notColours.value,
    notClarity: notClarities.value,
    notSymmetry: notSymmetries.value,
    isConsultant: isAdmin.value,
    category: [props.category],
    shape: props.shape ? [props.shape] : [...allowedShapes],
    country: countryCode.value,
    pagination: {
        page: props.page,
        limit: limit,
    },
}))

const variables: ComputedRef<GemsFindStonesQueryVariables> = useObjectComputed(
    (): GemsFindStonesQueryVariables => ({
        filters: filtersVariable.value,
        categories: [props.category],
        notColour: notColours.value,
        notClarity: notClarities.value,
        notSymmetry: notSymmetries.value,
        isConsultant: isAdmin.value,
        getBound: true,
        getStones: props.page < 99,
        locale: locale.value,
        currency: currency.value,
        country: countryCode.value!,
        contentPath:
            props.category === 'DIAMOND'
                ? '/sku-natural-diamonds'
                : '/sku-lab-grown-diamonds',
        facetedNav: {
            shape: filtersVariable.value.shape[0],
        },
    })
)

const response = useGemsFindStonesQuery({ variables })

const errors = useResponseError(response)

const { data, fetching, stale } = response

const findStones = toRef(() => data.value?.GemsApi_findStones)

const boundsInternal = toRef(() => data.value?.GemsApi_getBounds)

const bounds = useDiamondSearchBounds(boundsInternal)

const { shapes, ringShape } = useRingShapeConstraintForGemsAPI(bounds)

const hasBadFacets = computed((): boolean => {
    // const bounds = boundsInternal.value
    const bounds = boundsInternal.value

    return !!bounds && !!props.shape && !bounds.shape.includes(props.shape)
})

const priceBounds = usePriceBounds(
    bounds,
    () => props.shape,
    {
        min: 0,
        max: 25000,
    },
    defaultCurrency
)

const routeReset = computed(() =>
    routes.diamondSearch(props.category, ringShape.value?.systemCode)
)

const activeFilterCount = useFiltersCount(
    computed(() => [
        !ringShape.value && !!props.shape,
        !!filters.value.colour?.length,
        !!filters.value.clarity?.length,
        !!filters.value.polish?.length,
        !!filters.value.cut?.length,
        !!filters.value.symmetry?.length,
        !!filters.value.fluorescence?.length,
        !!filters.value.hasPhoto,
        !!filters.value.isCanadaMark,
        !!filters.value.withOriginCountry,
        !!(
            filters.value.priceInCurrency?.max ||
            filters.value.priceInCurrency?.min
        ),
        !!filters.value.weight,
        !!filters.value.depthPercent,
        !!filters.value.tablePercent,
        !!filters.value.ratio,
        !!(filters.value.length?.max || filters.value.length?.min),
        !!(filters.value.width?.max || filters.value.width?.min),
        !!(filters.value.depth?.max || filters.value.depth?.min),
        !!filters.value.isStock,
        !!filters.value.isDtlReport,
        !!filters.value.shippingFrom,
        !!filters.value.originCountry,
    ])
)

const filterChips = computed(() => {
    const chips: FilterChip[] = []

    if (formattedPriceRange.value) {
        chips.push({
            label: formattedPriceRange.value,
            onRemove: getClearFilter(filters, 'priceInCurrency'),
        })
    }

    if (!ringShape.value && props.shape) {
        chips.push({
            label:
                shapes.value?.find((s) => s.systemCode === props.shape)
                    ?.translation?.name || systemCodeToWords(props.shape),
            getRouteRemove: getRouteShape,
        })
    }

    if (filters.value.weight?.min && filters.value.weight?.max) {
        chips.push({
            label: `${t('diamond_search.filter.carat_size.chip', {
                min: formatNumericProperty(filters.value.weight.min),
                max: formatNumericProperty(filters.value.weight.max),
            })}`,
            onRemove: getClearFilter(filters, 'weight'),
        })
    }

    if (filters.value.cut) {
        filters.value.cut.forEach((cut) => {
            chips.push({
                label: t('diamond_search.filter.cut.chip', {
                    cut: replace_(cut),
                }),
                onRemove: getClearFilter(filters, 'cut', cut),
            })
        })
    }

    if (filters.value.colour) {
        filters.value.colour.forEach((colour) => {
            chips.push({
                label: t('diamond_search.filter.colour.chip', {
                    colour: replaceDIAMOND_(colour),
                }),
                onRemove: getClearFilter(filters, 'colour', colour),
            })
        })
    }

    if (filters.value.clarity) {
        filters.value.clarity.forEach((clarity) => {
            chips.push({
                label: t('diamond_search.filter.clarity.chip', {
                    clarity: replaceDIAMOND_(clarity),
                }),
                onRemove: getClearFilter(filters, 'clarity', clarity),
            })
        })
    }

    if (filters.value.polish) {
        filters.value.polish.forEach((polish) => {
            chips.push({
                label: t('diamond_search.filter.polish.chip', {
                    polish: replace_(polish),
                }),
                onRemove: getClearFilter(filters, 'polish', polish),
            })
        })
    }

    if (filters.value.symmetry) {
        filters.value.symmetry.forEach((symmetry) => {
            chips.push({
                label: t('diamond_search.filter.symmetry.chip', {
                    symmetry: replace_(symmetry),
                }),
                onRemove: getClearFilter(filters, 'symmetry', symmetry),
            })
        })
    }

    if (filters.value.fluorescence) {
        filters.value.fluorescence.forEach((fluorescence) => {
            chips.push({
                label: t('diamond_search.filter.fluorescence.chip', {
                    fluorescence: replace_(fluorescence),
                }),
                onRemove: getClearFilter(filters, 'fluorescence', fluorescence),
            })
        })
    }

    if (filters.value.depthPercent?.min && filters.value.depthPercent?.max) {
        chips.push({
            label: `${t('diamond_search.filter.depth_percent.chip', {
                min: formatNumericProperty(filters.value.depthPercent.min),
                max: formatNumericProperty(filters.value.depthPercent.max),
            })}`,
            onRemove: getClearFilter(filters, 'depthPercent'),
        })
    }

    if (filters.value.tablePercent?.max && filters.value.tablePercent?.min) {
        chips.push({
            label: `${t('diamond_search.filter.table_percent.chip', {
                min: formatNumericProperty(filters.value.tablePercent.min),
                max: formatNumericProperty(filters.value.tablePercent.max),
            })}`,
            onRemove: getClearFilter(filters, 'tablePercent'),
        })
    }

    if (filters.value.ratio?.min && filters.value.ratio?.max) {
        chips.push({
            label: `${t('diamond_search.filter.ratio.chip', {
                min: formatNumericProperty(filters.value.ratio.min),
                max: formatNumericProperty(filters.value.ratio.max),
            })}`,
            onRemove: getClearFilter(filters, 'ratio'),
        })
    }

    if (filters.value.deliveryDays) {
        chips.push({
            label: `${t('diamond_search.filter.deliveryDays.chip', {
                deliveryDays: filters.value.deliveryDays,
            })}`,
            onRemove: getClearFilter(filters, 'deliveryDays'),
        })
    }

    if (filters.value.hasPhoto) {
        chips.push({
            label: t('diamond_search.filter.photo.chip'),
            onRemove: getClearFilter(filters, 'hasPhoto'),
        })
    }

    if (filters.value.isCanadaMark) {
        chips.push({
            label: t('diamond_search.filter.canada_mark.chip'),
            onRemove: getClearFilter(filters, 'isCanadaMark'),
        })
    }

    if (filters.value.withOriginCountry) {
        chips.push({
            label: t('diamond_search.filter.origin.chip'),
            onRemove: getClearFilter(filters, 'withOriginCountry'),
        })
    }

    if (filters.value.depth?.min && filters.value.depth?.max) {
        chips.push({
            label: `${t('diamond_search.filter.depth.chip', {
                min: formatNumericProperty(filters.value.depth.min),
                max: formatNumericProperty(filters.value.depth.max),
            })}`,
            onRemove: getClearFilter(filters, 'depth'),
        })
    }

    if (filters.value.width?.min && filters.value.width?.max) {
        chips.push({
            label: `${t('diamond_search.filter.width.chip', {
                min: formatNumericProperty(filters.value.width.min),
                max: formatNumericProperty(filters.value.width.max),
            })}`,
            onRemove: getClearFilter(filters, 'width'),
        })
    }

    if (filters.value.length?.min && filters.value.length?.max) {
        chips.push({
            label: `${t('diamond_search.filter.length.chip', {
                min: formatNumericProperty(filters.value.length.min),
                max: formatNumericProperty(filters.value.length.max),
            })}`,
            onRemove: getClearFilter(filters, 'length'),
        })
    }

    if (filters.value.isDtlReport) {
        chips.push({
            label: t('diamond_search.filter.is_dtl.chip'),
            onRemove: getClearFilter(filters, 'isDtlReport'),
        })
    }

    if (filters.value.isStock) {
        chips.push({
            label: t('diamond_search.filter.is_stock.chip'),
            onRemove: getClearFilter(filters, 'isStock'),
        })
    }

    if (filters.value.shippingFrom) {
        filters.value.shippingFrom.forEach((country) => {
            chips.push({
                label: t('diamond_search.filter.shipping_from.chip', {
                    country: replace_(country),
                }),
                onRemove: getClearFilter(filters, 'shippingFrom', country),
            })
        })
    }

    if (filters.value.originCountry) {
        filters.value.originCountry.forEach((country) => {
            chips.push({
                label: t('diamond_search.filter.origin_country.chip', {
                    country: replace_(country),
                }),
                onRemove: getClearFilter(filters, 'originCountry', country),
            })
        })
    }

    return chips
})

const { isActivated } = useActivated()

const closeStoneModal = () => {
    if (!isActivated.value) {
        return
    }

    router.replace(
        routes.diamondSearch(
            props.category,
            props.shape,
            filters.value,
            props.page
        )
    )
}

const getStoneRoute = (identifier: string) =>
    routes.diamondSearch(
        props.category,
        props.shape,
        filters.value,
        props.page,
        identifier
    )

const paginationTo = (p: number) =>
    routes.diamondSearch(props.category, props.shape, filters.value, p)

const pageWatcher = usePageWatcher(
    () => props.page,
    () => findStones.value?.pagination.pages ?? 0,
    () => {
        return router.resolve(paginationTo(1)).fullPath
    },
    302
)

await response

useWatchRedirectImmediate(
    () => {
        return {
            ringShape: ringShape.value,
            propsShape: props.shape,
        }
    },
    ({ ringShape, propsShape }): void => {
        if (ringShape && ringShape.systemCode !== propsShape) {
            const route = router.resolve(getRouteShape(ringShape.systemCode))
            redirect(route.fullPath, 302)
        }
    }
)

await pageWatcher()
</script>
